import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ShuTongCharacter from './ShuTongCharacter';
import { useShuTongContext } from '../context/ShuTongContext';
import './ShuTongFab.css';

const STORAGE_KEY = 'shutong-fab-position';
const FAB_WIDTH = 72;
const FAB_HEIGHT = 88;

const HIDDEN_PATHS = new Set(['/', '/login', '/agent', '/study/agent']);

function getDefaultPosition() {
  return {
    x: Math.max(16, window.innerWidth - FAB_WIDTH - 20),
    y: Math.max(16, window.innerHeight - FAB_HEIGHT - 100)
  };
}

function getStoredPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultPosition();
    const parsed = JSON.parse(raw);
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return clampPosition(parsed);
    }
  } catch (e) {
    // ignore
  }
  return getDefaultPosition();
}

function clampPosition(pos) {
  const maxX = Math.max(8, window.innerWidth - FAB_WIDTH - 8);
  const maxY = Math.max(8, window.innerHeight - FAB_HEIGHT - 8);
  return {
    x: Math.min(Math.max(8, pos.x), maxX),
    y: Math.min(Math.max(8, pos.y), maxY)
  };
}

function ShuTongFab() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPoem } = useShuTongContext();
  const [position, setPosition] = useState(getStoredPosition);
  const dragState = useRef({
    dragging: false,
    moved: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0
  });

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => clampPosition(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const persistPosition = useCallback((pos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch (e) {
      // ignore
    }
  }, []);

  if (HIDDEN_PATHS.has(location.pathname)) {
    return null;
  }

  const handlePointerDown = (event) => {
    event.preventDefault();
    dragState.current = {
      dragging: true,
      moved: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const state = dragState.current;
    if (!state.dragging || state.pointerId !== event.pointerId) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;

    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      state.moved = true;
    }

    setPosition(clampPosition({
      x: state.originX + dx,
      y: state.originY + dy
    }));
  };

  const handlePointerUp = (event) => {
    const state = dragState.current;
    if (!state.dragging || state.pointerId !== event.pointerId) return;

    state.dragging = false;
    event.currentTarget.releasePointerCapture(event.pointerId);

    const finalPos = clampPosition({
      x: state.originX + (event.clientX - state.startX),
      y: state.originY + (event.clientY - state.startY)
    });
    setPosition(finalPos);
    persistPosition(finalPos);

    if (!state.moved) {
      navigate('/agent', {
        state: {
          selectedPoem: currentPoem,
          from: location.pathname,
          fromState: location.state
        }
      });
    }
  };

  return (
    <button
      type="button"
      className="shutong-fab"
      style={{ left: position.x, top: position.y }}
      aria-label="小书童 AI 助手"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <ShuTongCharacter className="shutong-fab-character" />
      <span className="shutong-fab-label">小书童</span>
    </button>
  );
}

export default ShuTongFab;
